import { useMemo, useState } from "react";
import { SlidersHorizontal } from "lucide-react";
import { Sheet, SheetContent, SheetTitle } from "../ui/sheet";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { ProductCard } from "./ProductCard";
import { Filters, EMPTY_FILTERS, applyFilters } from "./Filters";
import { Skeleton } from "../ui/skeleton";

const SORTS = { popular: "Most popular", newest: "Newest", "price-asc": "Price: low to high", "price-desc": "Price: high to low" };

const sorters = {
  popular: (a, b) => (b.sales_count || 0) - (a.sales_count || 0),
  newest: (a, b) => (b.created_at > a.created_at ? 1 : -1),
  "price-asc": (a, b) => a.price - b.price,
  "price-desc": (a, b) => b.price - a.price,
};

export const GridSkeleton = ({ n = 4 }) => (
  <div className="grid grid-cols-2 lg:grid-cols-4 gap-x-4 gap-y-10 sm:gap-x-6">
    {Array.from({ length: n }).map((_, i) => (
      <div key={i}>
        <Skeleton className="aspect-[4/5] rounded-3xl" />
        <Skeleton className="h-4 w-3/4 mt-4" />
      </div>
    ))}
  </div>
);

export const ProductListing = ({ products, loading, empty, initialType }) => {
  const [filters, setFilters] = useState({ ...EMPTY_FILTERS, types: initialType ? [initialType] : [] });
  const [sort, setSort] = useState("popular");
  const [mobile, setMobile] = useState(false);
  const all = useMemo(() => products || [], [products]);
  const shown = useMemo(() => applyFilters(all, filters).sort(sorters[sort]), [all, filters, sort]);

  if (loading) return <GridSkeleton n={8} />;
  if (!all.length) return empty;

  return (
    <div className="lg:grid lg:grid-cols-[240px_1fr] lg:gap-10">
      <aside className="hidden lg:block" data-testid="filters-sidebar">
        <Filters products={all} value={filters} onChange={setFilters} />
      </aside>
      <div>
        <div className="flex items-center justify-between gap-3 mb-6">
          <button onClick={() => setMobile(true)} className="lg:hidden flex items-center gap-2 h-11 px-4 rounded-full border-2 border-navy font-semibold text-navy" data-testid="mobile-filters-button">
            <SlidersHorizontal className="w-4 h-4" /> Filters
          </button>
          <p className="hidden lg:block text-sm text-navy/60" data-testid="results-count">{shown.length} product{shown.length !== 1 && "s"}</p>
          <Select value={sort} onValueChange={setSort}>
            <SelectTrigger className="w-[190px] h-11 rounded-full border-2" data-testid="sort-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {Object.entries(SORTS).map(([k, v]) => (
                <SelectItem key={k} value={k} data-testid={`sort-option-${k}`}>{v}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        {shown.length ? (
          <div className="grid grid-cols-2 xl:grid-cols-3 gap-x-4 gap-y-10 sm:gap-x-6" data-testid="product-grid">
            {shown.map((p) => <ProductCard key={p.slug} p={p} />)}
          </div>
        ) : (
          <div className="rounded-3xl bg-sand p-10 text-center" data-testid="no-filter-results">
            <p className="font-brush text-3xl text-navy">No matches for those filters</p>
            <button onClick={() => setFilters(EMPTY_FILTERS)} className="mt-4 rounded-full bg-navy text-white px-6 py-3 font-semibold" data-testid="clear-filters-button">Clear filters</button>
          </div>
        )}
      </div>
      <Sheet open={mobile} onOpenChange={setMobile}>
        <SheetContent side="bottom" className="max-h-[85vh] overflow-y-auto rounded-t-3xl" data-testid="mobile-filters-sheet">
          <SheetTitle className="font-brush text-3xl text-navy mb-4">Filters</SheetTitle>
          <Filters products={all} value={filters} onChange={setFilters} />
          <button onClick={() => setMobile(false)} className="sticky bottom-0 mt-6 w-full h-14 rounded-full bg-sun font-bold text-navy shadow-lg" data-testid="apply-filters-button">
            Show {shown.length} products
          </button>
        </SheetContent>
      </Sheet>
    </div>
  );
};
